import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Paper, Grid, InputBase } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { StoryCard, updateCard } from '../redux/cardDbSlice';
import DraggableSort from '../general/DraggableSort';
import { initial, getCardComponent, getCardSize, getStoryCardLines } from '../card_components';
import CardPad from './CardPad';
import { CardMenuAction, ShowMenuType } from './BookEditor';

interface LinePaperProps {
  lineWidth: number;
  lineHeight: number;
  cardNumber: number;
  cardWidth: number;
  selecting: boolean;
  dragging: boolean;
  invalidTransition: boolean;
}
const LinePaper = styled(Paper)<LinePaperProps>`
  position: absolute;
  min-width: 96%;
  width: ${props => props.lineWidth + 'px'};
  height: ${props => props.lineHeight + 'px'};
  padding-left: 8px;
  z-index: ${props => props.dragging ? 2 : 1};
  background-color: ${props => props.selecting ? '#f5f5f5' : undefined};
  pointer-events: ${props => props.dragging ? 'none' : undefined};
  transition: ${props => (props.dragging || props.invalidTransition) ? 'none' : 'transform 300ms'};
`
const CommentInputBase = styled(InputBase)`
  width: inherit;
`

export const getLineWidth = (card: StoryCard, children?: StoryCard[]) => {
  const minWidth = initial.cardWidth + 72;
  let width = getCardSize(card).width + 8;
  if (children) children.forEach(child => {
    width += getCardSize(child).width + 8;
  })
  return Math.max(width, minWidth);
}
export const getLineHeight = (card: StoryCard, children?: StoryCard[]) => {
  const linePadding = 32;
  let contentHeight = getCardSize(card).height;
  if (children) children.forEach(child => {
    const childHeight = getCardSize(child).height;
    if (childHeight > contentHeight) contentHeight = childHeight;
  })
  return contentHeight + linePadding;
}

type StorySheetProps = {
  ids: string[];
  cards: StoryCard[];
  cardMenuClicked: CardMenuAction;
  showMenu: ShowMenuType;
  setShowMenu: (value: ShowMenuType) => void;
  storyIndex?: number;
  setStoryIndex: (index: number) => void;
  scaleCoefficient: number;
}
const StorySheet: React.FC<StorySheetProps> = ({ids, cards, cardMenuClicked, showMenu, setShowMenu, storyIndex, setStoryIndex, scaleCoefficient}) => {
  const dispatch = useDispatch();
  const [rootIds, setRootIds] = useState<string[]>([]);
  const [rootCards, setRootCards] = useState<StoryCard[]>([]);
  type ChildrenHashType = { [key in string]: { ids: string[], cards: StoryCard[]} }
  const [childrenHash, setChildrenHash] = useState<ChildrenHashType>({});
  const [draggingId, setDraggingId] = useState<string>();
  const [invalidTransition, setInvalidTransition] = useState(false);
  const [headerTexts, setHeaderTexts] = useState<string[]>([]);

  useEffect(() => {
    const { rootIds: newRootIds, rootCards: newRootCards, childrenHash: newChildrenHash } = getStoryCardLines(ids, cards);
    setRootIds(newRootIds);
    setRootCards(newRootCards);
    setChildrenHash(newChildrenHash);
    
    const newHeaderTexts: string[] = [];
    newRootIds.forEach((id, ix) => newHeaderTexts.push(newRootCards[ix].headerText))
    setHeaderTexts(newHeaderTexts);
  }, [ids, cards])

  const dragStart = (ix: number) => {
    setStoryIndex(ix);
  }
  const changeOrder = (id: string, ix: number) => {
    const ids = rootIds;
    const list = rootCards;
    let newIx = ix;

    ids.forEach((targetId, targetIx) => {
      const value = list[targetIx];
      if (targetId === id) newIx = value.order;
      dispatch(updateCard({ id: targetId, order: value.order }));
    })
    setInvalidTransition(true);
    setTimeout(() => setInvalidTransition(false), 300)
    setStoryIndex(newIx);
  }
  const changeHeaderText = (id: string, ix: number, value: string) => {
    setHeaderTexts(pre => {
      pre[ix] = value;
      return pre;
    })
    dispatch(updateCard({ id, headerText: value }));
  }
  const getLinePosition = (card: StoryCard) => {
    let position = 0;
    const margin = 8;
    rootIds.forEach((id, ix) => {
      const target = rootCards[ix];
      const children = childrenHash[id] ? childrenHash[id].cards : undefined;
      if (!target || target.order >= card.order) return;
      position += getLineHeight(target, children) + margin;
    })
    return position;
  }

  return (
    <>
      { rootIds.map((id, ix) => {
        const card = rootCards[ix];
        const childrenCards = childrenHash[id] ? childrenHash[id].cards : undefined;
        const childrenIds = childrenHash[id] ? childrenHash[id].ids : undefined;
        const lineLength = childrenIds ? 1 + childrenIds.length : 1;
        const position = getLinePosition(card);
        return (
          <>
            <DraggableSort
              draggableProps={{
                position: { x: 0, y: position },
                axis: 'y', bounds: 'parent',
                onStart: () => dragStart(ix),
                onStop: () => changeOrder(id, ix),
                scale: scaleCoefficient
              }}
              targetClass='story-line'
              id={id} ids={rootIds} values={rootCards}
              setValues={setRootCards}
              setDraggingId={setDraggingId}
            >
              <LinePaper id={id} className='story-line'
                elevation={ix === storyIndex ? 1 : 0}
                lineWidth={getLineWidth(card, childrenCards)}
                lineHeight={getLineHeight(card, childrenCards)}
                cardNumber={lineLength} cardWidth={initial.cardWidth}
                selecting={ix === storyIndex} dragging={id === draggingId}
                invalidTransition={invalidTransition}
              >
                <div style={{width: '100%'}}>
                  { ix + 1 }
                  <div style={{position: 'absolute', top: 0, left: 48, width: initial.cardWidth}}>
                    <CommentInputBase margin="dense" value={headerTexts[ix]}
                      onChange={(ev) => changeHeaderText(id, ix, ev.currentTarget.value)}
                    />
                  </div>
                </div>
                <Grid container alignItems="flex-start" spacing={1} wrap="nowrap">
                  <Grid item>
                    <CardPad key={id} id={id}
                      cardMenuClicked={cardMenuClicked}
                      showMenu={showMenu} setShowMenu={setShowMenu}
                    >
                      { getCardComponent(card.kind, id) }
                    </CardPad>
                  </Grid>
                  { (childrenIds && childrenCards) && childrenIds.map((childId, childIx) => {
                    const childCard = childrenCards[childIx];
                    return (
                      <Grid item>
                        <CardPad key={childId} id={childId}
                          cardMenuClicked={cardMenuClicked}
                          showMenu={showMenu} setShowMenu={setShowMenu}
                        >
                          { getCardComponent(childCard.kind, childId) }
                        </CardPad>
                      </Grid>
                    );
                  }) }
                </Grid>
              </LinePaper>
            </DraggableSort>
          </>
        );
      }) }
    </>
  );
}

export default StorySheet;
